
import React, { useEffect, useState, useContext } from "react";
import {Routes, Route, Navigate, useNavigate } from "react-router-dom";
import moment from 'moment';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown } from "@fortawesome/free-solid-svg-icons";

import {
    ComposedChart,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip, Legend,
    ResponsiveContainer,
    AreaChart,
    Area,
    PieChart,
    Pie,
    LineChart,
    Line,
    ReferenceLine,
    linearGradient
} from 'recharts';
import Card from "../../shared/components/Card";
import Flex from "../../shared/components/Flex";
import Button from "../../shared/components/Button";
import Text from "../../shared/components/Text";
import useDevice from "../../shared/hooks/useDevice";
import Spacer from "../../shared/components/Spacer";
import ImgFolder from "../../assets/img/img_folder.png";

const ctg = [
    {type:"line",tag:"라인"},
    {type:"bar",tag:"막대"},
    {type:"area",tag:"영역"},
]

function Chart(props) {
    const { config } = props;
    const [ colNotData, setColNotData] = useState(false);
    const device = useDevice();
    const isMobile = device !== "desktop";

    useEffect(() => {
        const filter = props.data.filter(ele => {
            return ele[config.lineConfig[0].dataKey] !== null;
        });

        if (filter.length) {
            setColNotData(false);
        } else {
            setColNotData(true);
        }
    },[props.data,props.chartInfo])

    const formatTimeTick = (time) => {
        return `${moment.utc(time).format("DD일")} ${moment.utc(time).format("HH:mm")}`;
    }

    return (
        <>
            <Card
                    shadow={0}
                    padding={10}
                    backgroundColor="rgba(247, 248, 250, 1)"
                    style={{flex: 1, minWidth: "410px"}}
                >
                <Flex justifyContent="space-between" alignItems="center">
                {props.chartInfo?
                    (
                        <Flex flexDirection={isMobile ? "column" : "row"} justifyContent={isMobile ? "flex-end" : "space-between"}>
                            <Card
                                border={0}
                                shadow={0}
                                borderRadius={isMobile ? 1000 : 12}
                                fullWidth={isMobile}
                                width="fit-content"
                                backgroundColor="rgba(230, 230, 230, 1)"
                                padding={4}
                            >
                                    <Flex alignItems="center" noWrap={isMobile} justifyContent="center">
                                        {
                                            props.chartInfo.tab.map((ele,idx) => {
                                                return (
                                                    <Button
                                                        height={31}
                                                        paddingY={0}
                                                        fullWidth={isMobile}
                                                        borderRadius={isMobile ? 1000 : 8}
                                                        border={0}
                                                        key={idx}
                                                        backgroundColor={idx === props.chartInfo.select ? "white" : "rgba(230, 230, 230, 1)"}
                                                        onClick={() => props.setChartInfo({select:idx,tab:props.chartInfo.tab})}
                                                    >
                                                        <Text
                                                            variant="span"
                                                            weight={"semibold"}
                                                            color={idx === props.chartInfo.select ? "black" : "rgba(85, 85, 85, 1)"}
                                                        >
                                                            {ele.name}
                                                        </Text>
                                                    </Button>
                                                )
                                            })
                                        }
                                    </Flex>
                            </Card>
                        </Flex>

                    ):(
                        <Flex justifyContent="space-between" alignItems="center">
                            <Flex height={39} justifyContent="center" alignItems="center">
                                <Text size={16} weight={"semibold"}>{props.title}</Text>
                            </Flex>
                        </Flex>
                    )
                }
                    <div style={{display: "flex", gap: 8, justifyContent: isMobile ? "flex-end" : "flex-start"}}>
                        {ctg.map(ele => {
                            return (
                                <Button
                                    key={ele.type}
                                    paddingX={18}
                                    paddingY={9}
                                    variant={config.chart === ele.type ? "primary" : "white"}
                                    shape="square"
                                    width={59}
                                    height={34}
                                    onClick={(e => {
                                        props.setChart(ele.type);
                                    })}
                                    >
                                    <Text variant="span" size={13} weight="bold">
                                        {ele.tag}
                                    </Text>
                                </Button>
                            )
                        })}
                    </div>
                </Flex>
                
                <Spacer direction="vertical" size={10}/>
                <Card
                    className="chart_container"
                    shadow={0}
                    height={"245"}
                >
                    {props.data.length && !colNotData?
                        <ResponsiveContainer width="100%" height="100%">
                            <ComposedChart  
                                data={props.data}
                                margin={{ top: 20, right: 40, left: -10, bottom: 10 }}
                            >
                                <XAxis 
                                    dataKey={config.xData} 
                                    tickFormatter={formatTimeTick}
                                    {...config.XAxis}
                                />
                                <YAxis {...config.YAxis}/>
                                <Tooltip labelFormatter={(value) => moment.utc(value).format("MM월DD일 HH:mm")}/>
                                <Legend/>
                                <CartesianGrid />
                                {
                                    config.lineConfig.map(e => {
                                        if (e.lineType == 'line') {
                                            return (<Line dot={false} strokeWidth={2} {...e} key={e.dataKey}/>)

                                        }

                                        if (e.lineType == 'bar') {
                                            return (
                                                <Bar
                                                    strokeWidth={1}
                                                    barSize={10}
                                                    fillOpacity={0.9}
                                                    isAnimationActive={false}
                                                    {...e}
                                                    key={e.dataKey}
                                                />
                                            )
                                        }

                                        if (e.lineType == 'area') {
                                            return (<Area strokeWidth={1} {...e} key={e.dataKey}/>)
                                        }
                                    })
                                }
                                {config.referenceLineY && <ReferenceLine {...config.referenceLineY}/>}
                            </ComposedChart>
                        </ResponsiveContainer>
                        : 
                        <Flex alignItems="center" justifyContent="center" height="100%" gap={10} flexDirection="column"
                            style={{
                                flex: 1,
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                color: "#88888a",
                                minHeight: "220px"
                            }}
                        >
                            <img alt="" src={ImgFolder}/>
                            <Text size={16} weight={"semibold"}>데이터가 없습니다.</Text>
                        </Flex>
                    }
                </Card>
            </Card>
        </>
    )
}

export default Chart;
